import { Minus, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import FaqChat from '../../../components/FaqChat';
import { listFaq, type FaqEntryPublic } from '../../../api/faqApi';
import { useAuth } from '../../../context/AuthContext';
import MarketingShell from '../../marketing/MarketingShell';
import theme from '../../marketing/theme.module.css';
import styles from './FAQPage.module.css';

/**
 * The public FAQ, at "/faq" (SRS #19, #56).
 *
 * KN approved their own landing design and asked the rest of the public site
 * to follow it, so this page wears the shared shell and theme rather than the
 * application's chrome. Nothing about what it *does* changed: the list is
 * still the administrator-curated knowledge base with the shipped list as a
 * fallback, the category filter still narrows it, and the assistant above it
 * still answers questions the list does not.
 *
 * The accordion matches the one on the landing page — one answer open at a
 * time, a plus that becomes a minus — because a visitor arriving here from
 * there should not have to learn a second control that does the same job.
 */

const faqs = [
  {
    question: 'Can I customize alerts, summaries, and analyses?',
    answer:
      'Yes. Policyora alerts, summaries, and analyses are fully customizable. You control alert criteria, summary formats, analysis instructions, and memo formats.',
  },
  {
    question: 'Which jurisdictions and materials are covered?',
    answer:
      'Policyora covers federal and state jurisdictions across legislative and regulatory branches and analyzes hearings, bills, public comments, rules, and more.',
  },
  {
    question: 'How fast are hearing summaries available?',
    answer:
      'Policyora writes full summaries and analysis of public meetings minutes after streams end. Speaker identification is automated without relying on human transcripts.',
  },
  {
    question: 'Do you support structured pilots?',
    answer: 'Yes. We offer structured pilots so your team can validate performance against existing workflows before committing long term.',
  },
  {
    question: 'How can I learn about pricing?',
    answer: 'For tailored pricing information, contact sales@policyora.ai and we will design the right plan for your organization.',
  },
];

export const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // SRS #19: the page is driven by the administrator-curated knowledge base;
  // the shipped list below is the fallback while it loads or if the API is down.
  const { user } = useAuth();
  const [entries, setEntries] = useState<FaqEntryPublic[] | null>(null);
  const [category, setCategory] = useState('');
  useEffect(() => { listFaq().then(r => setEntries(r.entries)).catch(() => setEntries(null)); }, []);
  const categories = useMemo(() => Array.from(new Set((entries ?? []).map(e => e.category))), [entries]);
  const shown = useMemo(() => (entries ? entries.filter(e => !category || e.category === category) : faqs.map((f, i) => ({ id: i, category: 'General', ...f }))), [entries, category]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <MarketingShell>
      <section className={theme.hero}>
        <div className={`${theme.wrap} ${styles.head}`}>
          <p className={theme.eyebrow}>
            <span className={theme.eyebrowDash} aria-hidden="true" />
            Got questions?
          </p>
          <h1 className={theme.h1}>Frequently Asked Questions</h1>
          <p className={theme.lead}>
            How Policyora pairs policy expertise with AI to keep you informed. Still have a
            question? Write to hello@policyora.ai.
          </p>
        </div>
      </section>

      <section className={theme.band}>
        <div className={`${theme.wrap} ${styles.body}`}>
          <section className={styles.askCard} aria-label="Ask a question">
            <FaqChat canEscalate={!!user} />
          </section>

          {categories.length > 1 && (
            <div className={styles.filters}>
              {['', ...categories].map((c) => (
                <button
                  key={c || 'all'}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={category === c}
                  className={`${styles.pill} ${category === c ? styles.pillOn : ''}`}
                >
                  {c || 'All topics'}
                </button>
              ))}
            </div>
          )}

          <ul className={styles.faqList}>
            {shown.map((faq, index) => (
              <li key={faq.question} className={styles.faqItem}>
                <button
                  type="button"
                  id={`faq-q-${index}`}
                  className={styles.faqQ}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-a-${index}`}
                  onClick={() => handleToggle(index)}
                >
                  <span>{faq.question}</span>
                  {openIndex === index
                    ? <Minus size={20} aria-hidden="true" />
                    : <Plus size={20} aria-hidden="true" />}
                </button>
                {openIndex === index && (
                  <p
                    id={`faq-a-${index}`}
                    role="region"
                    aria-labelledby={`faq-q-${index}`}
                    className={styles.faqA}
                  >
                    {faq.answer}
                    {(faq as FaqEntryPublic).link
                      ? <> <a className={styles.answerLink} href={(faq as FaqEntryPublic).link as string}>Open →</a></>
                      : null}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </MarketingShell>
  );
};

export default FAQPage;
